import { useMemo, useState, Fragment } from "react";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ImportDialog } from "@/components/common/ImportDialog";
import { AddStockDialog } from "@/components/inventory/AddStockDialog";
import { IMPORTS } from "@/config/imports";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, PackagePlus, FileSpreadsheet, Layers } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const nf = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const STATUS_CLS = {
  IN_STOCK: "border-emerald-200 bg-emerald-50 text-emerald-700",
  LOW_STOCK: "border-amber-200 bg-amber-50 text-amber-700",
  OUT_OF_STOCK: "border-rose-200 bg-rose-50 text-rose-700",
};
const QUICK = [["ALL", "All"], ["IN_STOCK", "In Stock"], ["LOW_STOCK", "Low"], ["OUT_OF_STOCK", "Out"],
  ["RESERVED", "Reserved"], ["EXPIRING", "Expiring"], ["EXPIRED", "Expired"]];
const SORTS = {
  "name_asc": ["Ingredient A → Z", (a, b) => a.name.localeCompare(b.name)],
  "name_desc": ["Ingredient Z → A", (a, b) => b.name.localeCompare(a.name)],
  "cat_asc": ["Category A → Z", (a, b) => (a.category || "").localeCompare(b.category || "")],
  "sup_asc": ["Supplier A → Z", (a, b) => (a.supplier || "").localeCompare(b.supplier || "")],
  "cur_asc": ["Current Low → High", (a, b) => a.currentStock - b.currentStock],
  "cur_desc": ["Current High → Low", (a, b) => b.currentStock - a.currentStock],
  "avail_desc": ["Available High → Low", (a, b) => b.available - a.available],
  "val_desc": ["Value High → Low", (a, b) => b.value - a.value],
  "exp_asc": ["Expiry Nearest → Latest", (a, b) => (a.nearestExpiry || "9999").localeCompare(b.nearestExpiry || "9999")],
};

export default function StoreInventory() {
  const { role } = useAuth();
  const canAdd = ["super_admin", "stores_manager", "store_executive"].includes(role);
  const hideValues = ["stores_manager", "store_executive"].includes(role);
  const { data, refetch } = useResource("/inventory");
  const rows = Array.isArray(data) ? data : [];

  const [q, setQ] = useState("");
  const [quick, setQuick] = useState("ALL");
  const [cat, setCat] = useState("");
  const [sup, setSup] = useState("");
  const [brand, setBrand] = useState("");
  const [sort, setSort] = useState("name_asc");
  const [group, setGroup] = useState("none");
  const [importOpen, setImportOpen] = useState(false);
  const [addFor, setAddFor] = useState(undefined); // item or null(=generic)
  const [detail, setDetail] = useState(null);

  const cats = useMemo(() => [...new Set(rows.map((r) => r.category).filter(Boolean))].sort(), [rows]);
  const sups = useMemo(() => [...new Set(rows.map((r) => r.supplier).filter(Boolean))].sort(), [rows]);
  const brands = useMemo(() => [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (s && !["name", "ingredientId", "brand", "supplier", "category", "subcategory"].some((k) => String(r[k] || "").toLowerCase().includes(s))) return false;
      if (cat && r.category !== cat) return false;
      if (sup && r.supplier !== sup) return false;
      if (brand && (r.brand || "") !== brand) return false;
      if (quick === "IN_STOCK" && r.status !== "IN_STOCK") return false;
      if (quick === "LOW_STOCK" && r.status !== "LOW_STOCK") return false;
      if (quick === "OUT_OF_STOCK" && r.status !== "OUT_OF_STOCK") return false;
      if (quick === "RESERVED" && !(r.reserved > 0)) return false;
      if (quick === "EXPIRING" && r.expiryFlag !== "EXPIRING") return false;
      if (quick === "EXPIRED" && r.expiryFlag !== "EXPIRED") return false;
      return true;
    });
    out = [...out].sort(SORTS[sort][1]);
    return out;
  }, [rows, q, cat, sup, brand, quick, sort]);

  const groups = useMemo(() => {
    if (group === "none") return [["", filtered]];
    const keyer = { category: "category", supplier: "supplier", brand: "brand", status: "status" }[group];
    const map = {};
    filtered.forEach((r) => { const k = r[keyer] || "—"; (map[k] = map[k] || []).push(r); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, group]);

  const doExport = async (fmt) => {
    try {
      const params = new URLSearchParams({ format: fmt, search: q, status: quick === "ALL" ? "" : quick, category: cat, supplier: sup, brand });
      const res = await api.get(`/inventory/export?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `inventory.${fmt === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  const openDetail = async (r) => {
    setDetail({ loading: true, row: r });
    try { const { data } = await api.get(`/inventory/${r.ingredientId}/detail`); setDetail({ ...data, row: r }); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not load detail")); setDetail(null); }
  };

  const totalValue = filtered.reduce((s, r) => s + (r.value || 0), 0);

  const Row = ({ r }) => (
    <tr data-testid={`inv-row-${r.ingredientId}`} className="cursor-pointer hover:bg-amber-50/40" onClick={() => openDetail(r)}>
      <td className="px-3 py-2"><div className="font-medium text-stone-800">{r.name}</div><div className="font-mono text-[10px] text-stone-400">{r.ingredientId}</div></td>
      <td className="px-3 py-2 text-xs">{r.category || "—"}</td>
      <td className="px-3 py-2 text-xs">{r.supplier || "—"}</td>
      <td className="px-3 py-2 text-xs">{r.unit}</td>
      <td className="px-3 py-2 text-right tabular-nums">{nf(r.currentStock)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-violet-700">{nf(r.reserved)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold">{nf(r.available)}</td>
      {!hideValues && <td className="px-3 py-2 text-right tabular-nums">{money(r.value)}</td>}
      <td className="px-3 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLS[r.status] || "border-stone-200 bg-stone-50 text-stone-600"}`}>{(r.status || "").replace(/_/g, " ")}</span>{r.expiryFlag && <span className="ml-1 rounded bg-rose-100 px-1 text-[9px] text-rose-700">{r.expiryFlag}</span>}</td>
    </tr>
  );

  return (
    <div className="space-y-5">
      <PageHeader title="Store / Inventory" subtitle="Ledger-backed inventory · reflects stock recorded in the system (not assumed physical stock)">
        <div className="flex flex-wrap gap-2">
          {canAdd && <Button data-testid="inv-add-stock" className="gap-1.5 bg-amber-700 hover:bg-amber-800" onClick={() => setAddFor(null)}><PackagePlus className="h-4 w-4" /> Add Stock</Button>}
          <Button data-testid="inv-import" variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)}><FileSpreadsheet className="h-4 w-4" /> Import / Template</Button>
          <Button data-testid="inv-export-excel" variant="outline" className="gap-1.5" onClick={() => doExport("excel")}><Download className="h-4 w-4" /> Excel</Button>
          <Button data-testid="inv-export-csv" variant="outline" className="gap-1.5" onClick={() => doExport("csv")}><Download className="h-4 w-4" /> CSV</Button>
        </div>
      </PageHeader>

      <div className="space-y-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-9" placeholder="Search name, ID, brand, supplier, category…" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map(([k, l]) => (
            <button key={k} data-testid={`inv-quick-${k}`} onClick={() => setQuick(k)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${quick === k ? "border-amber-700 bg-amber-700 text-white" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <select data-testid="inv-filter-category" value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 rounded-md border border-stone-200 bg-white px-2 text-xs"><option value="">All Categories</option>{cats.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          <select data-testid="inv-filter-supplier" value={sup} onChange={(e) => setSup(e.target.value)} className="h-9 rounded-md border border-stone-200 bg-white px-2 text-xs"><option value="">All Suppliers</option>{sups.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          <select data-testid="inv-filter-brand" value={brand} onChange={(e) => setBrand(e.target.value)} className="h-9 rounded-md border border-stone-200 bg-white px-2 text-xs"><option value="">All Brands</option>{brands.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          <select data-testid="inv-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border border-stone-200 bg-white px-2 text-xs">{Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}</select>
          <select data-testid="inv-group" value={group} onChange={(e) => setGroup(e.target.value)} className="h-9 rounded-md border border-stone-200 bg-white px-2 text-xs"><option value="none">No grouping</option><option value="category">Group: Category</option><option value="supplier">Group: Supplier</option><option value="brand">Group: Brand</option><option value="status">Group: Status</option></select>
        </div>
        <div className="text-xs text-stone-500">{filtered.length} of {rows.length} items{!hideValues && <> · total value <b className="text-stone-700">{money(totalValue)}</b></>}</div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="inventory-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>{["Ingredient", "Category", "Supplier", "Unit", "Current", "Reserved", "Available", ...(hideValues ? [] : ["Value"]), "Status"].map((h, i) => <th key={h} className={`px-3 py-2.5 ${i >= 4 && h !== "Status" ? "text-right" : ""}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {filtered.length === 0 ? <tr><td colSpan={hideValues ? 8 : 9} className="px-3 py-8 text-center text-stone-400">No matching inventory.</td></tr> :
              groups.map(([gname, items]) => (
                <Fragment key={`grp-${gname}`}>
                  {gname !== "" && <tr className="bg-stone-100/70"><td colSpan={hideValues ? 8 : 9} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-600"><Layers className="mr-1 inline h-3 w-3" />{gname} — {items.length} items</td></tr>}
                  {items.map((r) => <Row key={r.ingredientId} r={r} />)}
                </Fragment>
              ))}
          </tbody>
        </table>
      </div>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} config={IMPORTS["/raw-material-inventory"]} onDone={refetch} />
      <AddStockDialog open={addFor !== undefined} onOpenChange={(o) => !o && setAddFor(undefined)} mode="rm" item={addFor} onDone={refetch} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white sm:max-w-2xl" data-testid="inv-detail">
          <DialogHeader><DialogTitle>{detail?.row?.name} <span className="font-mono text-xs text-stone-400">{detail?.row?.ingredientId}</span></DialogTitle>
            <DialogDescription className="text-xs">Stock summary and recent ledger movements for this item.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-2">
                {[["Category", detail.row.category], ["Subcategory", detail.row.subcategory], ["Brand", detail.row.brand || "—"],
                  ["Supplier", detail.row.supplier || "—"], ["Unit", detail.row.unit], ["Status", (detail.row.status || "").replace(/_/g, " ")],
                  ["Current", nf(detail.row.currentStock)], ["Reserved", nf(detail.row.reserved)], ["Available", nf(detail.row.available)],
                  ...(hideValues ? [] : [["Stock Value", money(detail.row.value)], ["Current Price", money(detail.row.currentPrice)], ["Last Purchase", money(detail.row.lastPurchasePrice)]])].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-stone-200 bg-stone-50/60 px-2.5 py-1.5"><div className="font-mono text-sm font-bold text-stone-800">{v}</div><div className="text-[9px] font-semibold uppercase text-stone-500">{k}</div></div>
                ))}
              </div>
              {canAdd && <Button size="sm" className="gap-1.5 bg-amber-700 hover:bg-amber-800" onClick={() => { setAddFor(detail.row); setDetail(null); }}><PackagePlus className="h-3.5 w-3.5" /> Add Stock for this item</Button>}
              <div>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-stone-500">Recent Stock Movements</div>
                <div className="max-h-52 overflow-auto rounded-lg border border-stone-200">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 text-left text-[10px] uppercase text-stone-400"><tr>{["Date", "Transaction", "Qty", "User", "Reference"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-stone-100">
                      {(detail.movements || []).length === 0 ? <tr><td colSpan={5} className="px-2 py-4 text-center text-stone-400">No movements.</td></tr> :
                        detail.movements.map((m, i) => (
                          <tr key={i}><td className="px-2 py-1">{(m.date || "").slice(0, 10)}</td><td className="px-2 py-1">{m.type}</td>
                            <td className={`px-2 py-1 tabular-nums ${m.quantity < 0 ? "text-rose-600" : "text-emerald-700"}`}>{m.quantity > 0 ? "+" : ""}{nf(m.quantity)}</td>
                            <td className="px-2 py-1 text-stone-500">{m.user || "—"}</td><td className="px-2 py-1 text-stone-500">{m.reference || "—"}</td></tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
